import * as Sentry from "@sentry/vue";

/**
 * Sentry bootstrap, shared by every Vue island of this plugin.
 *
 * @warn This file exists TWICE — components/newform/vue-src/sentry.js and
 * components/ticketshop/vue-src/sentry.js — and the `npm run sentry` script of
 * both components builds into the SAME artifact (assets/js/sentry.js). Only one
 * of the two can be deployed, whichever was built last wins. Keep both files
 * byte-identical, otherwise a rebuild of the other component silently reverts
 * changes made here.
 *
 * @warn CI does NOT rebuild this bundle. .gitlab-ci.yml only builds newform,
 * ticketshop, pricelist and setlist, so assets/js/sentry.js is a hand-built
 * artifact that has to be committed alongside a change to this file.
 */

const DSN = "https://ddebbd3af6212d3073cf6a466172b43f@o4507419068858368.ingest.de.sentry.io/4508686712504400";

const TICK_MS = 2000;
const FOLLOW_UP_MS = 8000;
// One verdict per page load is enough — it describes the page, not the single
// error. Keeps the added volume bounded on high-traffic noise (the Instagram
// in-app browser alone produces four-digit event counts, FEW-FRONTEND-VUE-9H).
const MAX_FOLLOW_UPS = 1;

/**
 * Main-thread health probe.
 *
 * @gotcha Chrome on iOS and the Google in-app browser inject their own scripts
 * into every page (autofill / translate scanners). Those scripts walk object
 * graphs without a visited set and overflow the stack on the first cycle they
 * meet — jQuery alone is enough, because `$.fn.constructor === $`. The
 * resulting RangeError arrives through window.onerror with EVERY frame
 * attributed to the document URL and its own line numbering, so it cannot be
 * told apart from our own code by filename. 100 % of those events come from
 * iOS (Sentry FEW-FRONTEND-VUE-32 and its fingerprint variants); measured DOM
 * depth of the affected pages is 22-23, so our markup does not provoke it.
 *
 * We deliberately do NOT filter these events. Instead we record whether OUR app
 * survived them, so "harmless noise or actual breakage?" can be answered from
 * data. Everything collected here is structural — timings, counters, mount
 * state. No input values, no key codes, no text content, no URLs beyond what
 * Sentry already stores.
 */
const probe = {
    ticks: 0,
    lastTickAt: Date.now(),
    worstGapMs: 0,
    interactions: 0,
    appErrors: 0,
    unattributedErrors: 0,
    followUpsSent: 0,
};

const startedAt = Date.now();

/** Every Vue app that ran through initSentry(), for liveness checks. */
const apps = [];

let watchdogStarted = false;

/**
 * A stalling interval is the cheapest proof that the main thread died: the
 * injected recursion runs synchronously, so a blocked or crashed thread stops
 * ticking while a merely-noisy error does not.
 */
function startWatchdog() {
    if (watchdogStarted) {
        return;
    }
    watchdogStarted = true;

    window.setInterval(function () {
        const now = Date.now();
        const gap = now - probe.lastTickAt - TICK_MS;
        if (gap > probe.worstGapMs) {
            probe.worstGapMs = gap;
        }
        probe.lastTickAt = now;
        probe.ticks += 1;
    }, TICK_MS);

    ["pointerdown", "keydown"].forEach(function (type) {
        window.addEventListener(type, function () {
            probe.interactions += 1;
        }, { passive: true, capture: true });
    });
}

/**
 * Legend that travels WITH every event.
 *
 * @claude Do not remove. Whoever reads a Sentry event years from now has this
 * file nowhere in reach, so the payload has to explain itself. Keep it in sync
 * when field names change.
 */
const PROBE_DOC = {
    what: "festiware JS health probe, set in kosmoskosmos/festiware-plugin — components/newform/vue-src/sentry.js (identical copy in components/ticketshop/vue-src/sentry.js).",
    why: "Chrome iOS and the Google in-app browser inject scripts that blow the stack on cyclic object graphs (jQuery is enough). Their frames all carry the document URL, so they look like our code. Nothing is filtered — instead this probe records whether OUR app survived.",
    js_origin: "unattributed = every stack frame pointed at the document URL, i.e. browser-injected or eval'd code, not one of our bundles. app = at least one frame came from a real script file.",
    liveness: "main_thread_ticks counts a 2s interval. If it stops advancing, the main thread is blocked or dead — that, not the error itself, is the breakage signal.",
    follow_up: "An unattributed error schedules a second event 8s later titled 'js-probe: …'. It carries origin_event_id pointing back to this event, and app_alive is one of alive / dead / unknown — 'unknown' means the probe could no longer read Vue's private internals and needs updating, NOT that anything broke.",
};

function snapshot() {
    return {
        main_thread_ticks: probe.ticks,
        ms_since_last_tick: Date.now() - probe.lastTickAt,
        worst_tick_delay_ms: Math.max(0, probe.worstGapMs),
        user_interactions: probe.interactions,
        errors_from_our_bundles: probe.appErrors,
        errors_unattributed: probe.unattributedErrors,
        page_uptime_ms: Date.now() - startedAt,
    };
}

/**
 * Mount state of our own Vue apps.
 *
 * @warn Reads the private Vue 3 internals `_instance` / `_container`, which are
 * the only synchronous way to see whether an app is still mounted. Guarded —
 * if a Vue upgrade moves them, the probe degrades to zeroes instead of throwing
 * inside beforeSend.
 */
function appHealth() {
    let mounted = 0;
    let connected = 0;
    let rendered = 0;
    let readable = 0;

    apps.forEach(function (app) {
        try {
            const instance = app && app._instance;
            const container = app && app._container;

            if (instance || container) {
                readable += 1;
            }
            if (instance && !instance.isUnmounted) {
                mounted += 1;
            }
            if (container && container.isConnected) {
                connected += 1;
                if (container.childElementCount > 0) {
                    rendered += 1;
                }
            }
        } catch (e) {
            // private internals moved — nothing to report for this app
        }
    });

    return {
        vue_apps_total: apps.length,
        vue_apps_mounted: mounted,
        vue_apps_in_dom: connected,
        vue_apps_rendered: rendered,
        // 0 while apps exist means the Vue internals could not be read at all —
        // the liveness verdict is then "unknown", never "dead".
        vue_apps_readable: readable,
    };
}

/**
 * Frames of injected or eval'd code carry the document URL (or nothing at all)
 * instead of a script URL. Note that beforeSend sees RAW filenames — the
 * October asset combiner's /combine/<hash> URLs and our /plugins/... paths —
 * not the in-app paths Sentry shows after symbolication.
 */
function isUnattributedFrame(frame) {
    const name = frame && (frame.filename || frame.abs_path);
    if (!name) {
        return true;
    }

    const bare = String(name).split("#")[0].split("?")[0];
    const href = String(location.href).split("#")[0];

    return bare === href
        || bare === href.split("?")[0]
        || bare === location.pathname
        || bare === "undefined"
        || bare === "<anonymous>"
        || bare === "";
}

/** True when every frame points at the document itself. */
function isUnattributed(event) {
    const values = event && event.exception && event.exception.values;
    if (!values || !values.length) {
        return false;
    }

    let seen = 0;

    for (let i = 0; i < values.length; i += 1) {
        const frames = (values[i].stacktrace && values[i].stacktrace.frames) || [];
        for (let j = 0; j < frames.length; j += 1) {
            seen += 1;
            if (!isUnattributedFrame(frames[j])) {
                return false;
            }
        }
    }

    return seen > 0;
}

/**
 * The event itself only shows the moment of the crash. What we actually want to
 * know is whether the page still worked afterwards, so we send one follow-up a
 * few seconds later and compare.
 */
function scheduleFollowUp(originEventId) {
    if (probe.followUpsSent >= MAX_FOLLOW_UPS) {
        return "suppressed-cap-reached";
    }
    probe.followUpsSent += 1;

    const before = Object.assign(snapshot(), appHealth());

    window.setTimeout(function () {
        const after = Object.assign(snapshot(), appHealth());

        const ticking = after.main_thread_ticks > before.main_thread_ticks;
        const continued = after.user_interactions > before.user_interactions;

        // A probe that cannot read Vue's internals knows nothing — reporting
        // that as "dead" would turn a future Vue upgrade into a false alarm.
        const blind = after.vue_apps_total > 0 && after.vue_apps_readable === 0;
        const verdict = blind
            ? "unknown"
            : (after.vue_apps_rendered > 0 && ticking ? "alive" : "dead");

        const title = {
            alive: "js-probe: our app survived an unattributed error (browser-injected code)",
            dead: "js-probe: our app looks DEAD after an unattributed error",
            unknown: "js-probe: cannot read Vue internals — probe needs updating",
        }[verdict];

        // Distinct titles on purpose: the Sentry issue title alone then states
        // the finding, and the bad case can carry its own alert rule.
        Sentry.captureMessage(
            title,
            {
                level: verdict === "dead" ? "error" : (verdict === "unknown" ? "warning" : "info"),
                tags: {
                    js_probe: "followup",
                    app_alive: verdict,
                    main_thread_ticking: String(ticking),
                    user_continued: String(continued),
                    origin_event_id: String(originEventId || "unknown"),
                },
                contexts: {
                    probe_doc: PROBE_DOC,
                    probe_before: before,
                    probe_after: after,
                },
            }
        );
    }, FOLLOW_UP_MS);

    return "scheduled";
}

/**
 * Never drops an event — it only classifies and annotates. Any exception in
 * here would swallow the very error we are trying to report, hence the catch.
 */
function beforeSend(event) {
    try {
        if (event.tags && event.tags.js_probe) {
            return event;
        }

        const unattributed = isUnattributed(event);

        event.tags = Object.assign({}, event.tags, {
            js_origin: unattributed ? "unattributed" : "app",
        });
        event.contexts = Object.assign({}, event.contexts, {
            probe: Object.assign(snapshot(), appHealth()),
            probe_doc: PROBE_DOC,
        });

        if (unattributed) {
            probe.unattributedErrors += 1;
            event.tags.probe_followup = scheduleFollowUp(event.event_id);
        } else {
            probe.appErrors += 1;
        }
    } catch (e) {
        // diagnostics must never break error reporting
    }

    return event;
}

window.initSentry = function (app) {
    if (app) {
        apps.push(app);
    }

    startWatchdog();

    Sentry.init({
        app,
        dsn: DSN,
        tracesSampleRate: 1.0,
        ignoreErrors: [
            // Android in-app/WebView JS bridges (Instagram/FB/Telegram etc.)
            // throw "Error invoking postEvent: Method not found" from injected
            // glue code — never from our own app or trackers. Pure noise.
            /Error invoking postEvent: Method not found/,
        ],
        beforeSend: beforeSend,
    });
}

/**
 * Report a swallowed (non-throwing) error to Sentry from the other bundles.
 *
 * Needed because this file is its own parcel bundle: newform.js / ticketshop.js
 * only ever see `window.initSentry`, and importing @sentry/vue there would
 * duplicate the whole SDK into those bundles. There is also no captureConsole
 * integration above, so a plain console.error never leaves the browser — code
 * that deliberately degrades instead of throwing (a failed dynamic import, a
 * skipped normalization) has to say so explicitly through here.
 *
 * Callers must stay working without it (`typeof window.reportFewException ===
 * 'function'`): this bundle is loaded by the theme, not by the plugin, so a
 * page can legitimately run without it.
 */
window.reportFewException = function (error, context) {
    Sentry.captureException(error, context);
}
